import { Directory, Filesystem } from '@capacitor/filesystem'
import { spErrorTags } from '@eventr/common'
import { ColoursObj, useToken } from '@eventr/common-storybook'
import { BoxProps, LinearProgress } from '@mui/material'
import axios, { AxiosRequestConfig } from 'axios'
import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export interface ISlideImageProps extends BoxProps {
    src: string
    addLoading?: boolean
    onLoad?: () => void
    onError?: () => void
    noGradient?: boolean
}

export const SlideImage = (props: ISlideImageProps) => {
    const [isLoading, setIsLoading] = useState(false)
    // we need make image request here using axios so that auth token is sent
    // and set the image src to the response

    const linearGradient = `linear-gradient(0deg, ${ColoursObj.backgrounds.header}D9,  ${ColoursObj.backgrounds.header}10)`

    const ref = useRef<HTMLDivElement | HTMLImageElement>(null)
    useEffect(() => {
        if (!ref) return

        if (!props.src) {
            if (props.component === 'img') {
                if (ref.current instanceof HTMLImageElement) ref.current.src = props.src
            }

            if (props.component !== 'img') {
                if (ref.current && ref.current.style) {
                    ref.current.style.backgroundImage = ''
                }
            }
            return
        }
        // if src is blob or data url, just set it
        if (props.src.startsWith('blob:') || props.src.startsWith('data:')) {
            if (props.component === 'img') {
                if (ref.current instanceof HTMLImageElement) ref.current.src = props.src
            }

            if (props.component !== 'img') {
                if (ref.current)
                    ref.current.style.backgroundImage = `${linearGradient}, url(${props.src})`
            }
            return
        }

        const loadImage = async () => {
            //remove the domain from the url
            const filePath = props.src.replace(/^(?:\/\/|[^/]+)*\//, '')
            try {
                const cacheImage = await Filesystem.readFile({
                    path: filePath,
                    directory: Directory.Cache,
                })

                if (!cacheImage.data) return

                if (ref.current) {
                    ref.current.style.backgroundImage = `${linearGradient}, url(data:image/png;base64,${cacheImage.data})`
                }
            } catch (error) {
                setIsLoading(true)
                axios
                    .get(props.src, {
                        responseType: 'arraybuffer',
                        headers: {
                            Authorization: `Bearer ${useToken
                                .getState()
                                .getToken()}`,
                        },
                    } as AxiosRequestConfig<any>)
                    .then((response) => {
                        const base64 = btoa(
                            new Uint8Array(response.data).reduce(
                                (data, byte) =>
                                    data + String.fromCharCode(byte),
                                ''
                            )
                        )

                        Filesystem.writeFile({
                            path: filePath,
                            data: base64,
                            directory: Directory.Cache,
                            recursive: true,
                        })

                        setIsLoading(false)

                        if (ref.current) {
                            ref.current.style.backgroundImage = `${linearGradient}, url(data:image/png;base64,${base64})`
                        }
                    })
                    .catch((err) => {
                        setIsLoading(false)
                        spErrorTags('Error Fetching image', ['AuthImage'], err)
                        if (props.onError) {
                            props.onError()
                        }
                    })
            }
        }
        loadImage()
    }, [props.src, ColoursObj])

    // if loading add
    const { addLoading, children, style } = props
    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
            }}
        >
            <motion.div ref={ref} style={style}>
                {children}
            </motion.div>

            {addLoading && isLoading && (
                <LinearProgress
                    variant="query"
                    color="secondary"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        zIndex: 500,
                    }}
                />
            )}
        </div>
    )
}
